import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';

/**
 * Formatea segundos restantes como `mm:ss` con padding de dos dígitos en
 * AMBAS partes (900 → «15:00», 5 → «00:05»). Formato FIJADO por test
 * verbatim: si se cambia el padding hay que tocar el spec del countdown.
 */
export function formatMmSs(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Cuenta atrás reutilizable (Bloque 2: reserva flotante de precompra; el
 * Bloque 3 la reutiliza en la página de pago ONVO).
 *
 *  - Recibe `expiresAt` ABSOLUTO (ISO 8601 o Date) y calcula el restante
 *    contra `Date.now()` en cada tick — nunca decrementa un contador
 *    acumulado, así sobrevive a pestañas en background donde los timers se
 *    ralentizan (throttling del navegador).
 *  - SSR-safe con el patrón del repo: el `interval(1000)` solo arranca en
 *    browser (`afterNextRender`) y se limpia con `takeUntilDestroyed`. En
 *    servidor renderiza el restante estático calculado una única vez.
 *  - `label` admite el placeholder `{mm:ss}` (copy centralizado, p. ej.
 *    «Tienes {mm:ss} para completar tu reserva»); sin placeholder se muestra
 *    el `mm:ss` a secas.
 *  - Emite `expired` UNA vez por cada `expiresAt` al llegar a 0 (también si
 *    el timestamp ya venció al recibirlo, caso rehidratación).
 */
@Component({
  selector: 'app-countdown',
  templateUrl: './countdown.component.html',
  styleUrl: './countdown.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownComponent {
  private readonly destroyRef = inject(DestroyRef);

  /** Momento absoluto de expiración. `null` = countdown inactivo (no renderiza). */
  readonly expiresAt = input<string | Date | null>(null);

  /** Plantilla con `{mm:ss}` (o texto sin placeholder para mostrar solo el tiempo). */
  readonly label = input<string>('{mm:ss}');

  /** Se emite UNA vez por `expiresAt` cuando el restante llega a 0. */
  readonly expired = output<void>();

  /** Reloj: se actualiza cada segundo en browser (fuente del recálculo absoluto). */
  private readonly now = signal(Date.now());

  /** Guard de emisión única: timestamp objetivo para el que ya se emitió `expired`. */
  private expiredEmittedFor: number | null = null;

  /** Timestamp objetivo en ms epoch, o `null` si no hay expiración válida. */
  private readonly targetMs = computed<number | null>(() => {
    const raw = this.expiresAt();
    if (!raw) return null;
    const ms = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
    return Number.isNaN(ms) ? null : ms;
  });

  /** Segundos restantes (≥ 0), SIEMPRE derivados del timestamp absoluto. */
  readonly remainingSeconds = computed(() => {
    const target = this.targetMs();
    if (target === null) return 0;
    return Math.max(0, Math.ceil((target - this.now()) / 1000));
  });

  /** Restante formateado `mm:ss` (p. ej. «14:59», «00:05»). */
  readonly mmss = computed(() => formatMmSs(this.remainingSeconds()));

  /** Texto final a pintar: el label con `{mm:ss}` ya interpolado. */
  protected readonly text = computed(() => this.label().replace('{mm:ss}', this.mmss()));

  constructor() {
    // Tick de 1 s solo en browser. El recálculo usa Date.now(), así que si el
    // navegador ralentiza el interval (pestaña en background), el primer tick
    // al volver ya muestra el restante REAL (no acumula deriva).
    afterNextRender(() => {
      interval(1000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.now.set(Date.now()));
    });

    // Expiración: emite una única vez por cada `expiresAt`. Cambiar a un
    // timestamp nuevo (reserva nueva) re-arma la emisión.
    effect(() => {
      const target = this.targetMs();
      if (target === null) {
        this.expiredEmittedFor = null;
        return;
      }
      if (this.remainingSeconds() > 0) return;
      if (this.expiredEmittedFor === target) return;
      this.expiredEmittedFor = target;
      this.expired.emit();
    });
  }
}
